import { useEffect, useState } from 'react';
import { Loader2, MessageCircle, Pencil, Trash2, X } from 'lucide-react';
import { NotificationSettings } from '@property/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  useSendWhatsAppOtp,
  useVerifyWhatsAppOtp,
  useRemoveWhatsAppNumber,
} from '@/hooks/use-notification-settings';

const DEFAULT_COUNTRY_CODE = '+91';

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

export function WhatsAppNotificationCard({
  settings,
  enabled,
  onToggle,
}: {
  settings: NotificationSettings;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
}) {
  const { phoneNumber, whatsappVerified } = settings;
  const sendOtp = useSendWhatsAppOtp();
  const verifyOtp = useVerifyWhatsAppOtp();
  const removeNumber = useRemoveWhatsAppNumber();

  // Always starts collapsed — the user opens the panel (via the pencil icon) when
  // they actually want to add, change, or remove a number.
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(!whatsappVerified);
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [localNumber, setLocalNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [sentPhoneNumber, setSentPhoneNumber] = useState<string | null>(null);

  // Re-sync the default editing state (and collapse back down) whenever verification
  // status changes, without fighting a user's manual pencil/X clicks in between.
  useEffect(() => {
    setOpen(false);
    setEditing(!whatsappVerified);
    setCountryCode(DEFAULT_COUNTRY_CODE);
    setLocalNumber('');
    setSentPhoneNumber(null);
    setOtpCode('');
  }, [whatsappVerified]);

  const fullPhoneNumber = `${countryCode.trim()} ${localNumber}`.trim();

  const handleSendOtp = () => {
    sendOtp.mutate(fullPhoneNumber, {
      onSuccess: () => {
        setSentPhoneNumber(fullPhoneNumber);
      },
    });
  };

  const handleVerify = () => {
    if (!sentPhoneNumber) return;
    verifyOtp.mutate(
      { phoneNumber: sentPhoneNumber, code: otpCode },
      {
        onSuccess: () => {
          // whatsappVerified doesn't change on a "change number" re-verify (it was
          // already true), so the effect above never fires to reset the form back to
          // the (collapsed) default — reset explicitly here so the new number shows
          // in the header without needing a refresh. Collapses the panel rather than
          // leaving it open — the toast + updated header text is feedback enough.
          setEditing(false);
          setOpen(false);
          setCountryCode(DEFAULT_COUNTRY_CODE);
          setLocalNumber('');
          setOtpCode('');
          setSentPhoneNumber(null);
        },
      },
    );
  };

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-xl border border-border bg-card shadow-sm"
    >
      <div className="flex flex-wrap items-center gap-3 p-4 sm:p-5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
          <MessageCircle className="h-5 w-5 text-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-foreground">WhatsApp</div>
          <div className="truncate text-sm text-muted-foreground">
            {whatsappVerified ? phoneNumber : 'Not connected'}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={open ? 'Collapse WhatsApp settings' : 'Expand WhatsApp settings'}
            >
              {open ? <X className="h-4 w-4" /> : <Pencil className="h-3.5 w-3.5" />}
            </Button>
          </CollapsibleTrigger>
          {whatsappVerified && (
            <Switch checked={enabled} onCheckedChange={onToggle} aria-label="WhatsApp" />
          )}
        </div>
      </div>

      <CollapsibleContent>
        <div className="space-y-4 border-t border-border p-4 sm:p-5">
          {!editing ? (
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="button" variant="outline" onClick={() => setEditing(true)}>
                Change number
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => removeNumber.mutate()}
                disabled={removeNumber.isPending}
              >
                {removeNumber.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Remove number
              </Button>
            </div>
          ) : (
            <>
              <Label className="text-sm font-medium">
                {whatsappVerified
                  ? 'Change WhatsApp number'
                  : 'Add a WhatsApp number to get updates and alerts'}
              </Label>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[8rem_1fr]">
                <div className="space-y-1.5">
                  <Label htmlFor="whatsappCountryCode" className="text-xs text-muted-foreground">
                    Country Code
                  </Label>
                  <Input
                    id="whatsappCountryCode"
                    type="tel"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    placeholder="+91"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="whatsappNumber" className="text-xs text-muted-foreground">
                    Phone Number
                  </Label>
                  <Input
                    id="whatsappNumber"
                    type="tel"
                    inputMode="numeric"
                    value={localNumber}
                    onChange={(e) => setLocalNumber(digitsOnly(e.target.value))}
                  />
                </div>
              </div>

              <Button
                type="button"
                onClick={handleSendOtp}
                disabled={localNumber.length < 10 || !countryCode.trim() || sendOtp.isPending}
                className="w-full sm:w-auto"
              >
                {sendOtp.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send OTP'}
              </Button>
              <p className="text-sm text-muted-foreground">
                We&apos;ll send a 6-digit code to verify your number.
              </p>

              {sentPhoneNumber && (
                <div className="space-y-3 rounded-md border border-border bg-muted/40 p-3">
                  <p className="text-xs text-muted-foreground">
                    We sent a 6-digit code to {sentPhoneNumber} on WhatsApp.
                  </p>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Input
                      inputMode="numeric"
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => setOtpCode(digitsOnly(e.target.value))}
                      placeholder="6-digit code"
                      className="sm:w-40"
                    />
                    <Button
                      type="button"
                      onClick={handleVerify}
                      disabled={otpCode.length !== 6 || verifyOtp.isPending}
                      className="w-full sm:w-auto"
                    >
                      {verifyOtp.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Verify'
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
